/**
 * The Data step's columns when the composite has a field mapping (CT-5480).
 *
 * The mapping says **which** fields the composite exposes and **where** each
 * comes from; the members' own `field/list` says what each field **is** (type,
 * system role, record key). This joins the two:
 *
 *   - a mapped field becomes a column with the composite's label and its
 *     origin (`table`, `role`), on top of the member field's metadata;
 *   - a field of a non-PRIMARY member is a column like any other when
 *     `allowExtension` (the default today, see
 *     `ALLOW_EXTENSION_MEMBER_FIELDS`); with the flag off it is set aside as
 *     `unsupportedColumns` — shown, never selectable;
 *   - **only mapped fields become columns.** The primary's other physical
 *     columns are not offered: the mapping is what the composite exposes, and
 *     a filter or grouper on something outside it is the builder inventing a
 *     field the product does not publish. The one exception never reaches a
 *     picker — see `recordKeyColumn` below.
 *
 * With no mapping at all it returns `null`, and the caller keeps the primary
 * member's full column list — today's behaviour, unchanged.
 *
 * @param {{
 *   mappedFields: object[],
 *   columnsByEntity: Record<string, object[]>,
 *   primaryEntityUuid: string,
 *   allowExtension?: boolean,
 * }} input
 * @returns {{ columns: object[], unsupportedColumns: object[],
 *   recordKeyColumn: object|null } | null}
 */
export function resolveMappedColumns({
  mappedFields = [],
  columnsByEntity = {},
  primaryEntityUuid,
  allowExtension = false,
}) {
  if (!mappedFields.length) return null;

  const metaByField = new Map(
    Object.values(columnsByEntity).flat().map((column) => [column.fieldUuid, column]),
  );

  const columns = [];
  const unsupportedColumns = [];

  mappedFields.forEach((field) => {
    const meta = metaByField.get(field.fieldUuid);
    const column = {
      ...(meta ?? { fieldUuid: field.fieldUuid, internalName: field.alias, type: 'text', dataType: '' }),
      displayName: field.label || meta?.displayName || field.alias,
      alias: field.alias,
      // The table is what the picker shows; the two uuids are what the
      // payload needs to declare this field's source in the query graph —
      // `entityUuid` to name the source and `memberUuid` to join it.
      // The table is what the picker shows; the rest is what the payload needs
      // to declare this field's source and the join that reaches it —
      // `memberUuid` for another table of this composite, the relationship
      // and its direction for a field of a neighbouring one.
      origin: field.origin
        ? {
          table: field.origin.table || field.origin.tableName || field.origin.tableCode,
          role: field.origin.role,
          entityUuid: field.origin.entityUuid,
          memberUuid: field.origin.memberUuid,
          relationshipUuid: field.origin.relationshipUuid,
          direction: field.origin.direction,
        }
        : null,
    };
    const isPrimary = !field.origin || field.origin.role === 'PRIMARY';
    if (isPrimary || allowExtension) columns.push(column);
    else unsupportedColumns.push(column);
  });

  /*
   * The primary's `<base>_uuid`, kept **out** of `columns`.
   *
   * A record-set type binds `row_key` to it and `publish` answers
   * `DSW012 {missing: bindings}` without it, but nobody maps a record uuid
   * and nobody should see one in a filter or a column picker. So it travels
   * on its own, and only `buildWidgetPayload` reads it.
   */
  const mappedUuids = new Set(columns.map((column) => column.fieldUuid));
  const recordKeyColumn = (columnsByEntity[primaryEntityUuid] ?? [])
    .find((column) => column.isRecordKey && !mappedUuids.has(column.fieldUuid)) ?? null;

  return { columns, unsupportedColumns, recordKeyColumn };
}

export default resolveMappedColumns;
